import React, { useState, useEffect } from 'react';
import { Popover } from 'antd';
import { renderMarkdownToHtml } from 'Utils/formatting';
import {
    VersionBadgeStyled,
    DiffLineInnerStyled,
    LineBadgeSlotStyled,
    SelectionMenuStyled,
    SelectionMenuHeaderStyled,
    SelectionMenuTitleStyled,
    SelectionMenuActionsStyled,
    PopoverContentStyled,
    PopoverDividerStyled,
    SmartDiffContainer,
    DiffPane,
    PaneHeader,
    PaneContent,
} from './SbDiffCheckerV3.styles';
import { SbButton, SbTooltip, TooltipTriggerStyled } from 'uiCore';
import SbInput from 'uiCore/SbInput/SbInput';
import { STATUS_CLASS_MAP, DECISION, VIEW_MODE } from 'constants/jobs.constants';
import { centerInPane } from './diffViewUtils';
import './SmartDiffViewerV3.scss';

const TOOLTIP_ACCEPT_CHANGE = "Accept this change. The modified text will be retained in the published version.";
const TOOLTIP_REJECT_CHANGE = "Reject this change. Flag it for the creator to revise. The creator will see this block highlighted in Surgical Edit mode.";

// ── SmartDiffViewerV3 ──────────────────────────────────────────────────────────
// Port of SmartDiffViewer.jsx — Ant Design Popover instead of Bootstrap overlay.
// All styles in SbDiffCheckerV3.styles.js (no inline styles, no Bootstrap classes).
// feedback (Draft Feedback, Ownership design part 3): { isCommentable(item), popoverFor(item, close), threadsFor(item),
//   badgeFor(item) (optional: a button at the line's top right), onLineClick(item) (optional) }
// — a changed sentence of the draft opens the comment box instead of the approve / request-changes one, and its
// comments sit under it. No decisions in that mode.


const SmartDiffViewerV3 = ({
    diffData,
    onDecisionChange,
    onClearDecision,
    viewMode,
    readOnly = false,
    feedback = null,
}) => {
    const [selectionData, setSelectionData] = useState({ ids: [], position: null });

    useEffect(() => {
        const handleScroll = () => {
            if (selectionData.position) setSelectionData({ ids: [], position: null });
        };
        const panes = document.querySelectorAll('.pane-content');
        panes.forEach((p) => p.addEventListener('scroll', handleScroll));
        window.addEventListener('scroll', handleScroll);
        return () => {
            panes.forEach((p) => p.removeEventListener('scroll', handleScroll));
            window.removeEventListener('scroll', handleScroll);
        };
    }, [selectionData.position]);

    if (!diffData?.left_pane || !diffData?.right_pane) return null;

    const handleMouseUp = (e) => {
        if (readOnly || feedback) return;
        if (e.target.closest('.approval-popover')) return;

        const selection = window.getSelection();
        const selectedText = selection.toString().trim();

        if (selectedText.length > 0) {
            const range = selection.getRangeAt(0);
            const affectedIds = [];
            document
                .querySelectorAll('.diff-pane.right .diff-line.diff-unchanged')
                .forEach((node) => {
                    if (selection.containsNode(node, true)) affectedIds.push(node.id);
                });
            if (affectedIds.length > 0) {
                const rect = range.getBoundingClientRect();
                setSelectionData({ ids: affectedIds, position: { top: rect.top - 80, left: rect.left } });
            }
        } else {
            setSelectionData({ ids: [], position: null });
        }
    };

    const getMirrorData = (linkedId, currentSide) => {
        if (!linkedId) return null;
        const list  = currentSide === 'left' ? diffData.right_pane : diffData.left_pane;
        const match = list.find((item) => item.element_id === linkedId);
        return match?.decision
            ? { decision: match.decision, comment: match.comment, isHistorical: match.isHistorical }
            : null;
    };

    const handleLineClick = (clickedId, linkedId) => {
        if (!clickedId || !linkedId) return;
        const clickedEl = document.getElementById(clickedId);
        const linkedEl  = document.getElementById(linkedId);
        if (!clickedEl || !linkedEl) return;
        document.querySelectorAll('.highlight-permanent').forEach((el) => el.classList.remove('highlight-permanent'));
        clickedEl.classList.add('highlight-permanent');
        linkedEl.classList.add('highlight-permanent');
        // only the linked line's own pane scrolls (scrollIntoView also moved the page and left it shifted down)
        centerInPane(linkedEl);
    };

    return (
        <SmartDiffContainer>

            {/* Left pane */}
            <DiffPane>
                {(viewMode === VIEW_MODE.APPROVER || viewMode === VIEW_MODE.FEEDBACK) && (
                    <PaneHeader>
                        <strong>Previous Version</strong>
                        {diffData.reference_version && (
                            <VersionBadgeStyled>
                                V{diffData.reference_version}
                                {diffData.reference_version_tag ? ` (${diffData.reference_version_tag})` : ''}
                            </VersionBadgeStyled>
                        )}
                    </PaneHeader>
                )}
                <PaneContent className="pane-content">
                    {diffData.left_pane.map((item) => (
                        <DiffLine
                            key={item.element_id}
                            item={item}
                            side="left"
                            readOnly={readOnly}
                            mirrorData={getMirrorData(item.linked_id, 'left')}
                            onDecisionChange={onDecisionChange}
                            onClearDecision={onClearDecision}
                            onLineClick={handleLineClick}
                            feedback={feedback}
                        />
                    ))}
                </PaneContent>
            </DiffPane>

            {/* Right pane */}
            <DiffPane>
                {(viewMode === VIEW_MODE.APPROVER || viewMode === VIEW_MODE.FEEDBACK) && (
                    <PaneHeader>
                        <strong>{viewMode === VIEW_MODE.FEEDBACK ? 'Shared Draft' : 'Current Version'}</strong>
                        {diffData.current_version && (
                            <VersionBadgeStyled>
                                V{diffData.current_version}
                                {diffData.current_version_tag ? ` (${diffData.current_version_tag})` : ''}
                            </VersionBadgeStyled>
                        )}
                    </PaneHeader>
                )}
                <PaneContent className="pane-content" onMouseUp={handleMouseUp}>
                    {diffData.right_pane.map((item) => (
                        <DiffLine
                            key={item.element_id}
                            item={item}
                            side="right"
                            readOnly={readOnly}
                            mirrorData={getMirrorData(item.linked_id, 'right')}
                            onDecisionChange={onDecisionChange}
                            onClearDecision={onClearDecision}
                            onLineClick={handleLineClick}
                            feedback={feedback}
                        />
                    ))}
                </PaneContent>
            </DiffPane>

            {selectionData.position && (
                <SelectionMenu
                    selectionData={selectionData}
                    onClose={() => setSelectionData({ ids: [], position: null })}
                    onDecisionChange={onDecisionChange}
                    onClearDecision={onClearDecision}
                />
            )}
        </SmartDiffContainer>
    );
};

// ── Multi-sentence selection floating menu ─────────────────────────────────────

const SelectionMenu = ({ selectionData, onClose, onDecisionChange, onClearDecision }) => {
    const [comment, setComment] = useState('');

    return (
        <SelectionMenuStyled
            $top={selectionData.position.top}
            $left={selectionData.position.left}
            className="approval-popover"
        >
            <SelectionMenuHeaderStyled>
                <SelectionMenuTitleStyled>
                    Review {selectionData.ids.length} Sentences
                </SelectionMenuTitleStyled>
                <SbButton type="text" size="small" onClick={onClose}>×</SbButton>
            </SelectionMenuHeaderStyled>
            <SbInput
                textarea
                rows={2}
                placeholder="Reason for review..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
            />
            <SelectionMenuActionsStyled>
                <SbButton
                    danger
                    size="small"
                    disabled={!comment.trim()}
                    onClick={() => {
                        onDecisionChange(selectionData.ids, DECISION.UNCHANGED, DECISION.REVIEW, comment);
                        onClose();
                    }}
                >
                    ✕ Request Changes
                </SbButton>
                <SbButton
                    size="small"
                    onClick={() => {
                        onClearDecision(selectionData.ids);
                        onClose();
                    }}
                >
                    Undo / Clear Selection
                </SbButton>
            </SelectionMenuActionsStyled>
        </SelectionMenuStyled>
    );
};

// ── Single diff line with Ant Design Popover ──────────────────────────────────

const DiffLine = ({ item, side, onDecisionChange, onClearDecision, onLineClick, readOnly, mirrorData, feedback = null }) => {
    const [comment, setComment] = useState(item.comment || '');
    const [open, setOpen]       = useState(false);

    const localData = item.decision
        ? { decision: item.decision, comment: item.comment, isHistorical: item.isHistorical }
        : null;
    const effectiveData = localData || mirrorData;

    let activeDecision = null;
    let displayComment = null;

    if (effectiveData) {
        const { decision, comment: dc, isHistorical } = effectiveData;
        if (!isHistorical || (decision === DECISION.REVIEW && side === 'left')) {
            activeDecision = decision;
            displayComment = dc;
        }
    }

    useEffect(() => {
        if (!open) {
            setComment(item.comment || '');
        }
    }, [item.comment, open]);

    const statusClass = (status) => STATUS_CLASS_MAP[status?.toLowerCase()] ?? 'diff-unchanged';

    const isActionableSide =
        (side === 'right' && item.status !== 'removed') ||
        (side === 'left'  && item.status === 'removed');
    const isCommentable = Boolean(feedback) && side === 'right' && feedback.isCommentable(item);
    const isInteractive = feedback ? isCommentable : (
        !readOnly &&
        (item.status !== 'unchanged' || activeDecision === DECISION.REVIEW) &&
        isActionableSide);
    const canSyncScroll = item.linked_id && item.status === 'modified';

    const handleSubmit = (decision) => {
        setOpen(false);
        onDecisionChange([item.element_id], item.status.toUpperCase(), decision, comment, item.text);
    };

    const popoverContent = feedback ? (isCommentable && feedback.popoverFor(item, () => setOpen(false))) : (
        <PopoverContentStyled>
            {item.status !== 'unchanged' && (
                <>
                    <SbTooltip content={TOOLTIP_ACCEPT_CHANGE}>
                        <TooltipTriggerStyled>
                            <SbButton
                                color="green"
                                variant="solid"
                                onClick={() => handleSubmit(DECISION.APPROVE)}
                            >
                               ✓ Approve
                            </SbButton>
                        </TooltipTriggerStyled>
                    </SbTooltip>
                    <PopoverDividerStyled>OR</PopoverDividerStyled>
                </>
            )}
            <SbInput
                textarea
                rows={2}
                placeholder="Reason..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
            />
            <SbTooltip content={TOOLTIP_REJECT_CHANGE}>
                <span>
                    <SbButton
                        color="red"
                        variant="solid"
                        disabled={!comment.trim()}
                        onClick={() => handleSubmit(DECISION.REVIEW)}
                    >
                        ✕ Request Changes
                    </SbButton>
                </span>
            </SbTooltip>
            {activeDecision && !item.isHistorical && (
                <SbButton
                    color="blue" 
                    variant="solid"
                    onClick={() => { setOpen(false); onClearDecision([item.element_id]); }}
                >
                    Undo Decision
                </SbButton>
            )}
        </PopoverContentStyled>
    );

    const badge = isCommentable && feedback.badgeFor ? feedback.badgeFor(item) : null;
    const className = [
        'diff-line',
        statusClass(item.status),
        isInteractive ? 'interactive' : '',
        canSyncScroll ? 'syncable' : '',
        activeDecision === DECISION.REVIEW && item.status !== 'modified' ? 'reviewed-bg' : '',
    ].filter(Boolean).join(' ');

    const content = (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- pre-existing; keyboard/a11y fix tracked in #2109
        <div
            id={item.element_id}
            className={className}
            style={isCommentable && badge ? { position: 'relative' } : undefined}
            onClick={() => {
                document.querySelectorAll('.highlight-permanent').forEach((el) => el.classList.remove('highlight-permanent'));
                if (canSyncScroll) onLineClick(item.element_id, item.linked_id);
                if (isCommentable) feedback.onLineClick?.(item);
            }}
        >
            {/* Draft Feedback: the comment button at the line's top right; the text keeps clear of it */}
            {isCommentable && badge && <LineBadgeSlotStyled>{badge}</LineBadgeSlotStyled>}
            <DiffLineInnerStyled style={isCommentable && badge ? { paddingRight: 64 } : undefined}>
                {item.ghostContext && (
                    <div className="ghost-internal-alert">
                        <span className="ghost-icon">👻</span>
                        <span>Was a Ghost Line. Previous Feedback: {item.ghostContext.comment}</span>
                    </div>
                )}
                {activeDecision && (
                    <span className={`status-pill ${activeDecision === DECISION.APPROVE ? 'status-approved' : 'status-reviewed'}`}>
                        {activeDecision === DECISION.APPROVE
                            ? <span>&#10003; APPROVED</span>
                            : <span>&#10005; REVIEWED: {displayComment}</span>
                        }
                    </span>
                )}
                <span
                    className="content-text"
                    dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(item.text) }}
                />
            </DiffLineInnerStyled>
        </div>
    );

    const line = isInteractive ? (
        <Popover
            content={popoverContent}
            trigger="click"
            open={open}
            onOpenChange={(nextOpen) => {
                if (nextOpen) setComment(item.comment || '');
                setOpen(nextOpen);
            }}
            placement="auto"
            getPopupContainer={(trigger) => trigger.parentElement}
        >
            {content}
        </Popover>
    ) : content;
    return isCommentable ? <>{line}{feedback.threadsFor(item)}</> : line;
};

export default SmartDiffViewerV3;
